import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    Output
} from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { OrganizationSearchResponse } from '../../../../../core/models/civi-crm/organization-search-response';
import { RegExPattern } from '../../../../../core/models/regexp-pattern/regex-pattern';
import { OrganizationService } from '../../../../../core/services/organization/organization.service';
import { HoverTooltipComponent } from '../../../../../shared/components/tooltips/hover-tooltip/hover-tooltip.component';
import { RegexInputDirective } from '../../../../../shared/directives/regex-input/regex-input.directive';
import { OrganizationChipComponent } from '../../user-review/components/organization-chip/organization-chip.component';
import { InfoText } from '../../user-review/constants/info-text';

@Component({
    selector: 'app-organization-search',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OrganizationSearchComponent),
            multi: true,
        },
    ],
    imports: [
        MatInputModule,
        MatFormFieldModule,
        MatIconModule,
        MatAutocompleteModule,
        OrganizationChipComponent,
        FormsModule,
        ReactiveFormsModule,
        HoverTooltipComponent,
        RegexInputDirective,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './organization-search.component.html',
})
export class OrganizationSearchComponent implements ControlValueAccessor {
    @Input() originalOrganizationName: string = '';
    @Output() organizationMatchChange = new EventEmitter<boolean>();

    organizationMatch: boolean = false;
    organizationName: string = '';
    filteredOrganizations: OrganizationSearchResponse[] = [];
    infoText = InfoText;
    regExPattern = RegExPattern;

    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private readonly organizationService: OrganizationService) {}

    writeValue(value: string): void {
        this.organizationName = value;
        this.checkOrganizationNameMatches();
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    onInputChange(value: string): void {
        this.organizationName = value;
        this.onChange(value);
        this.onTouched();
        this.checkOrganizationNameMatches();
    }

    revertOrganizationNameChanges() {
        this.organizationName = this.originalOrganizationName;
        this.onInputChange(this.organizationName);
    }

    checkOrganizationNameMatches() {
        if (!this.organizationName) {
            this.updateOrganizationMatch(false);
            return;
        }

        this.organizationService
            .searchOrganizations(this.organizationName)
            .then((organizations: OrganizationSearchResponse[]) => {
                this.filteredOrganizations = organizations;

                const match = organizations.some(
                    (organization) =>
                        organization.name === this.organizationName,
                );

                this.updateOrganizationMatch(match);
            });
    }

    private updateOrganizationMatch(isMatch: boolean) {
        if (this.organizationMatchValueHasChanged(isMatch)) {
            this.organizationMatch = isMatch;
            this.organizationMatchChange.emit(this.organizationMatch);
        }
    }

    /**
     * Checks whether the new match status is different than the currently set status.
     * We only want to emit changes to the status.
     */
    private organizationMatchValueHasChanged(isMatch: boolean) {
        return this.organizationMatch !== isMatch;
    }
}
